import { useI18n } from '@affine/i18n';
import { streamLainAnimationController } from '@blocksuite/affine/shared/utils';
import { setElementVars } from '@vanilla-extract/dynamic';
import { isEqual } from 'lodash-es';
import {
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

import { BrandLoader } from '../../brand';
import { Button } from '../../ui/button';
import { Loading } from '../../ui/loading';
import { notify } from '../../ui/notification';
import { useSettingsFrame } from './settings-frame';
import { SettingsPageReadinessContext } from './settings-page-readiness';
import {
  SettingsCard,
  SettingsRow,
  SettingsSectionFrame,
  type SettingsSectionFrameProps,
} from './settings-primitives';
import * as styles from './settings-states.css';

/* ── Empty, loading, error ───────────────────────────────── */

export interface SettingsEmptyProps {
  icon: ReactNode;
  title: ReactNode;
  description?: ReactNode;
  action?: ReactNode;
  testId?: string;
}

/** Shown where a list or card has nothing to show yet; names the next step. */
export const SettingsEmpty = ({
  icon,
  title,
  description,
  action,
  testId,
}: SettingsEmptyProps) => (
  <div className={styles.empty} data-testid={testId}>
    <span className={styles.emptyIcon} aria-hidden="true">
      {icon}
    </span>
    <div className={styles.emptyTitle}>{title}</div>
    {description ? (
      <div className={styles.emptyDescription}>{description}</div>
    ) : null}
    {action ? <div className={styles.emptyAction}>{action}</div> : null}
  </div>
);

const LOADING_REVEAL_MS = 150;
const LOADING_SLOW_MS = 1200;
const PAGE_LOADER_LOGO_SIZE = 96;

const LOGO_LEAVE: Keyframe[] = [{ opacity: 1 }, { opacity: 0 }];

/**
 * React removes the loading state the moment the content arrives. A copy of
 * the logo takes its place for the length of the exit motion, so the logo
 * leaves as softly as it came.
 */
function leaveSoftly(element: HTMLElement) {
  const parent = element.offsetParent;
  if (
    !(parent instanceof HTMLElement) ||
    !streamLainAnimationController.isEnabled(element)
  ) {
    return;
  }
  const copy = element.cloneNode(true) as HTMLElement;
  copy.removeAttribute('role');
  copy.removeAttribute('aria-busy');
  copy.removeAttribute('aria-label');
  copy.setAttribute('aria-hidden', 'true');
  copy.classList.add(styles.loadingLeaving);
  setElementVars(copy, {
    [styles.leavingTop]: `${element.offsetTop}px`,
    [styles.leavingLeft]: `${element.offsetLeft}px`,
    [styles.leavingWidth]: `${element.offsetWidth}px`,
    [styles.leavingHeight]: `${element.offsetHeight}px`,
  });
  parent.append(copy);
  void streamLainAnimationController
    .play(copy, LOGO_LEAVE, 'exit')
    .finished.then(() => copy.remove());
}

/** A short wait shows nothing; a long one is marked as slow. */
function useLoadingPhase() {
  const [phase, setPhase] = useState<'waiting' | 'shown' | 'slow'>('waiting');
  useEffect(() => {
    const reveal = window.setTimeout(
      () => setPhase('shown'),
      LOADING_REVEAL_MS
    );
    const slow = window.setTimeout(() => setPhase('slow'), LOADING_SLOW_MS);
    return () => {
      window.clearTimeout(reveal);
      window.clearTimeout(slow);
    };
  }, []);
  return phase;
}

/**
 * Stands for a whole settings page while it loads: the logo, centred in the
 * visible body. It fades in after a short wait and starts moving once the wait
 * gets long.
 */
export const SettingsPageLoader = () => {
  const t = useI18n();
  const ref = useRef<HTMLDivElement>(null);
  const phase = useLoadingPhase();
  const shown = phase !== 'waiting';
  const shownRef = useRef(shown);

  useLayoutEffect(() => {
    shownRef.current = shown;
    const element = ref.current;
    if (!shown || !element) return;
    const animation = streamLainAnimationController.play(
      element,
      [{ opacity: 0 }, { opacity: 1 }],
      'feedback'
    );
    return () => animation?.cancel();
  }, [shown]);

  useLayoutEffect(() => {
    const element = ref.current;
    return () => {
      if (element && shownRef.current) leaveSoftly(element);
    };
  }, []);

  return (
    <div
      ref={ref}
      className={styles.pageLoader}
      role="status"
      aria-busy="true"
      aria-label={t['com.streamlain.loading']()}
    >
      {shown ? (
        <BrandLoader size={PAGE_LOADER_LOGO_SIZE} animated={phase === 'slow'} />
      ) : null}
    </div>
  );
};

/** A part that loads after its page is shown: the common spinner, in place. */
const SettingsInlineLoading = () => {
  const t = useI18n();
  const shown = useLoadingPhase() !== 'waiting';
  return (
    <div
      className={styles.loading}
      role="status"
      aria-busy="true"
      aria-label={t['com.streamlain.loading']()}
    >
      {shown ? <Loading /> : null}
    </div>
  );
};

/**
 * Marks a part of a settings page that waits for its data. While the page
 * first loads, the part holds it back and the page shows its logo. A part that
 * loads after the page is shown gets the common spinner. Outside a page it
 * stands for the whole page.
 */
export const SettingsLoading = () => {
  const readiness = useContext(SettingsPageReadinessContext);
  const holdsPage = readiness !== null && !readiness.ready;
  const hold = readiness?.hold;
  useLayoutEffect(() => {
    if (!holdsPage || !hold) return;
    return hold();
  }, [holdsPage, hold]);
  if (!readiness) return <SettingsPageLoader />;
  return holdsPage ? null : <SettingsInlineLoading />;
};

export interface SettingsErrorProps {
  icon: ReactNode;
  title: ReactNode;
  description?: ReactNode;
  retryLabel?: ReactNode;
  onRetry?: () => void;
  testId?: string;
}

/** A page or card could not load. Offers a retry when the caller can repeat the request. */
export const SettingsError = ({
  icon,
  title,
  description,
  retryLabel,
  onRetry,
  testId,
}: SettingsErrorProps) => (
  <div role="alert">
    <SettingsEmpty
      icon={icon}
      title={title}
      description={description}
      testId={testId}
      action={
        onRetry ? (
          <Button variant="secondary" onClick={onRetry}>
            {retryLabel}
          </Button>
        ) : undefined
      }
    />
  </div>
);

/* ── Danger zone ─────────────────────────────────────────── */

/** Last section of a page; holds every action that deletes or cannot be undone. */
export const SettingsDangerZone = ({
  title,
  children,
  ...props
}: Omit<SettingsSectionFrameProps, 'title'> & { title?: ReactNode }) => {
  const t = useI18n();
  return (
    <SettingsSectionFrame
      {...props}
      title={title ?? t['com.streamlain.settings.ui.danger-zone']()}
    >
      <SettingsCard tone="danger">{children}</SettingsCard>
    </SettingsSectionFrame>
  );
};

export interface SettingsDangerRowProps {
  label: ReactNode;
  description?: ReactNode;
  actionLabel: ReactNode;
  /** Label of the second, confirming click. */
  confirmLabel: ReactNode;
  onConfirm: () => void | Promise<void>;
  disabled?: boolean;
  testId?: string;
}

/** A destructive action that needs a second, explicit click before it runs. */
export const SettingsDangerRow = ({
  label,
  description,
  actionLabel,
  confirmLabel,
  onConfirm,
  disabled,
  testId,
}: SettingsDangerRowProps) => {
  const t = useI18n();
  const [confirming, setConfirming] = useState(false);
  const [running, setRunning] = useState(false);
  const confirm = useCallback(() => {
    setRunning(true);
    Promise.resolve(onConfirm())
      .catch(error => console.error(error))
      .finally(() => {
        setRunning(false);
        setConfirming(false);
      });
  }, [onConfirm]);
  return (
    <SettingsRow label={label} description={description}>
      {confirming ? (
        <div className={styles.dangerRowAction}>
          <Button variant="secondary" onClick={() => setConfirming(false)}>
            {t['Cancel']()}
          </Button>
          <Button
            variant="error"
            loading={running}
            disabled={running}
            onClick={confirm}
            data-testid={testId ? `${testId}-confirm` : undefined}
          >
            {confirmLabel}
          </Button>
        </div>
      ) : (
        <Button
          variant="error"
          disabled={disabled}
          onClick={() => setConfirming(true)}
          data-testid={testId}
        >
          {actionLabel}
        </Button>
      )}
    </SettingsRow>
  );
};

/* ── Draft that saves itself ─────────────────────────────── */

export interface SettingsDraft<T> {
  draft: T;
  setDraft: (next: T | ((current: T) => T)) => void;
  /** Merges a partial change into the draft. */
  update: (patch: Partial<T>) => void;
  /** The draft differs from the saved value. */
  dirty: boolean;
  saving: boolean;
  /** Why the current draft could not be saved; cleared when saving again. */
  error: unknown;
  /** Saves the current draft again after a failure. */
  retry: () => void;
  /** Returns to the saved value. */
  discard: () => void;
}

export interface SettingsDraftOptions<T> {
  /** Only a valid draft is saved; an invalid one waits for the next edit. */
  isValid?: (draft: T) => boolean;
  /** How long edits rest before they are saved. */
  delayMs?: number;
  /** Replaces the generic failure text, e.g. with the server's reason. */
  describeError?: (cause: unknown) => string | undefined;
  /**
   * For choices that act at once, like a switch or a picked model: a failed
   * save returns the draft to the saved value instead of keeping it.
   */
  revertOnError?: boolean;
}

interface SubmittedDraft<T> {
  value: T;
  state: 'saving' | 'saved' | 'failed';
  /** A newer saved value arrived while the draft still was this value. */
  answered: boolean;
}

/**
 * Holds the edited copy of a form and saves it on its own once the edits
 * rest and the draft is valid; nobody has to press a save button. Each save
 * is confirmed briefly, a failed save keeps the draft and says why (or
 * returns to the saved value, see `revertOnError`). A newer saved value
 * replaces an untouched draft; the answer to an older save never replaces a
 * newer edit. Leaving the page saves pending edits; the settings dialog asks
 * before leaving only while the draft cannot be saved (invalid or failed).
 */
export function useSettingsDraft<T>(
  saved: T,
  onSave: (draft: T) => void | Promise<void>,
  {
    isValid,
    delayMs = 600,
    describeError,
    revertOnError = false,
  }: SettingsDraftOptions<T> = {}
): SettingsDraft<T> {
  const t = useI18n();
  const [draft, setDraft] = useState(saved);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const baseline = useRef(saved);
  // The draft last handed to onSave and how that went; it is not saved twice
  // on its own.
  const submitted = useRef<SubmittedDraft<T> | null>(null);
  // The draft of the last render, read when the saved value moves on.
  const shown = useRef(draft);
  shown.current = draft;
  const dirty = !isEqual(draft, saved);
  const valid = isValid ? isValid(draft) : true;
  const pending =
    dirty &&
    valid &&
    !(submitted.current && isEqual(submitted.current.value, draft));

  useLayoutEffect(() => {
    if (isEqual(baseline.current, saved)) return;
    // Server state moved on. While a save waits for its answer, only a draft
    // that still is exactly the sent value takes the new state: a draft back
    // at the old value is a newer choice of the user (A, B sent, back to A),
    // and the answer B must not replace it. Otherwise the draft follows while
    // it is untouched.
    const previous = baseline.current;
    baseline.current = saved;
    const current = shown.current;
    const sent = submitted.current;
    if (sent && sent.state !== 'failed' && !sent.answered) {
      if (!isEqual(current, sent.value)) return;
      sent.answered = true;
      setDraft(saved);
      return;
    }
    if (isEqual(current, previous)) setDraft(saved);
  }, [saved]);

  const latest = useRef({ onSave, describeError, revertOnError, t });
  latest.current = { onSave, describeError, revertOnError, t };
  const run = useCallback(async (value: T) => {
    const { onSave, describeError, revertOnError, t } = latest.current;
    const attempt: SubmittedDraft<T> = {
      value,
      state: 'saving',
      answered: false,
    };
    submitted.current = attempt;
    setSaving(true);
    setError(null);
    try {
      await onSave(value);
      attempt.state = 'saved';
      notify.success({ title: t['com.streamlain.settings.ui.saved']() });
    } catch (cause) {
      notify.error({
        title:
          describeError?.(cause) ??
          t['com.streamlain.settings.ui.save-failed'](),
      });
      if (revertOnError) {
        // The choice did not hold: show what is stored again, unless the
        // user has chosen anew meanwhile, and let the same choice be sent
        // again.
        if (submitted.current === attempt) submitted.current = null;
        setDraft(current =>
          isEqual(current, value) ? baseline.current : current
        );
      } else {
        attempt.state = 'failed';
        setError(cause);
      }
    } finally {
      setSaving(false);
    }
  }, []);

  useEffect(() => {
    if (!pending || saving) return;
    const timer = setTimeout(() => void run(draft), delayMs);
    return () => clearTimeout(timer);
  }, [pending, saving, draft, delayMs, run]);

  // Edits that were still resting when the form goes away are saved anyway.
  const unsaved = useRef({ pending, draft });
  unsaved.current = { pending, draft };
  useEffect(
    () => () => {
      if (unsaved.current.pending) void run(unsaved.current.draft);
    },
    [run]
  );

  const blocked = dirty && (!valid || Boolean(error));
  const blockedRef = useRef(blocked);
  useLayoutEffect(() => {
    blockedRef.current = blocked;
  }, [blocked]);
  const registerUnsavedGuard = useSettingsFrame()?.registerUnsavedGuard;
  useEffect(
    () => registerUnsavedGuard?.(() => blockedRef.current),
    [registerUnsavedGuard]
  );

  const retry = useCallback(() => void run(draft), [draft, run]);
  const discard = useCallback(() => {
    setError(null);
    setDraft(saved);
  }, [saved]);
  const update = useCallback(
    (patch: Partial<T>) => setDraft(current => ({ ...current, ...patch })),
    []
  );

  return { draft, setDraft, update, dirty, saving, error, retry, discard };
}
